import React, { Fragment } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Button, Grid, Paper, Typography } from '@material-ui/core';

import { EadNode, EvidenceUnit } from 'types';
import { ReadOnlyField } from 'components/read-only-field/read-only-field';
import { ArchivalUnitDetail } from 'modules/archival-unit/archival-unit-detail';
import { EvidenceUnitLabels, getSpaceOccupiedLabel } from 'enums';
import {
  decideRelationLabel,
  handleOriginatorLink,
} from 'modules/register/reqister-utils';
import { RegisterField } from 'modules/register/register-field';

import { GridCentralPanelProps } from 'components/grid/grid-types';
import CollapseSection from 'components/collapse-section';
import {
  LevelDefaultIcon,
  levelIcon,
} from 'components/grid/grid-hooks/breadcrumbs-hook';
import { useIntl } from 'react-intl';
import { NavToParentField } from 'components/grid/grid-hooks/item-hierarchy-context-hook';
import { LinkField } from 'components/link-field/link-field';
import { useEadTreeContext } from 'components/grid/grid-ctx/EadTreeCtx';
import { isDevelopment } from 'utils';
import { constructOriginatorLabel } from 'modules/register/utils/construct-originator-labels';
import { useCollapseSections } from 'components/collapse-section/collapse-section-hook';

const capitalize = (val: string) => val.charAt(0).toUpperCase() + val.slice(1);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    archivalUnitInfo: {
      marginTop: 10,
      fontWeight: 400,
      color: theme.palette.primary.main,
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    flexRow: {
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: 8,
    },
  })
);

type Props = {
  item?: EadNode;
} & Omit<GridCentralPanelProps, 'item'>;

export const EadFields = ({ item }: Props) => {
  const classes = useStyles();
  const { currentArchivalAid } = useEadTreeContext();
  const {
    handleCollapseAll,
    handleExpandAll,
    handleStateChange,
    allCollapsed,
    allExpanded,
    isExpanded,
    registerKey,
  } = useCollapseSections();

  const {
    title,
    levelLabel,
    level,
    langMaterial = [],
    publicCopy,
    relations,
    evidenceUnits,
  } = item ?? ((currentArchivalAid?.eadNode as EadNode) || {});

  const { formatMessage } = useIntl();

  if (publicCopy) {
    return <ArchivalUnitDetail item={publicCopy} parentEad={item} />;
  }

  const LevelIcon = level ? levelIcon?.[level] : LevelDefaultIcon;

  return (
    <>
      {isDevelopment() && <>DEV: EAD FIELDS</>}
      <Typography variant="h5">
        <div className={classes.flexRow}>
          <LevelIcon fontSize="small" className={classes.icon} />
          {title ?? item?.archivalAidMetadata?.name ?? ''}
        </div>
      </Typography>
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <Typography variant="h6">
            {formatMessage({
              id: 'archivalUnit.inclusion',
            })}
          </Typography>
          <br />
          <br />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollection',
            })}
            value={currentArchivalAid?.archivalCollection?.name}
            href={`fondy/0/${currentArchivalAid?.archivalCollection?.id}`}
          />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollectionNumber',
            })}
            value={currentArchivalAid?.archivalCollection?.evidenceNumber}
            href={`fondy/0/${currentArchivalAid?.archivalCollection?.id}`}
          />
          {currentArchivalAid && (
            <LinkField
              label={formatMessage({
                id: 'archivalUnit.archivalAid',
              })}
              value={currentArchivalAid?.name}
              href={`pomucky/0/${currentArchivalAid?.id}`}
            />
          )}
          <ReadOnlyField label="Úroveň" value={levelLabel ?? level} />
          <ReadOnlyField label="Jazyk" value={langMaterial.join(', ')} />
          <ReadOnlyField label="Kopie" value={item?.altFormAvail} />

          <NavToParentField />
        </Grid>
      </Paper>
      <Grid
        container
        className={classes.root}
        style={{ justifyContent: 'flex-end', paddingBottom: 0 }}
      >
        <Button
          disabled={!allCollapsed()}
          onClick={() => {
            handleCollapseAll();
          }}
        >
          Sbalit vše
        </Button>
        <Button
          disabled={!allExpanded()}
          onClick={() => {
            handleExpandAll();
          }}
        >
          Rozbalit vše
        </Button>
      </Grid>
      {/* IDENTIFIKACE */}
      <Paper elevation={0} className={classes.paper}>
        <CollapseSection
          title="Identifikace"
          id="identification"
          overrideOpen={isExpanded('identification')}
          registerKey={registerKey}
          onStateChange={handleStateChange}
        >
          <Grid container className={classes.root}>
            <br />
            <br />
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.referenceDesignation',
              })}
              value={item?.structuredLink}
            />
            <ReadOnlyField
              label="Číslo archiválie"
              value={item?.numericId?.toString()}
            />
            <ReadOnlyField
              label="Pořadové číslo"
              value={
                item?.referenceLabels?.find(
                  (rl) => rl.type === 'PORADOVE_CISLO'
                )?.value
              }
            />
            {item &&
              item?.referenceLabels
                ?.filter(
                  (rl) =>
                    rl.type !== 'PORADOVE_CISLO' &&
                    rl.type !== 'REFERENCNI_OZNACENI'
                )
                ?.map((rl) => (
                  <ReadOnlyField
                    key={rl.type}
                    label={capitalize(rl.label)}
                    value={rl.value}
                  />
                ))}
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.storageUnit',
              })}
              value={
                (item?.storageUnits?.length ?? 0) > 0
                  ? item?.storageUnits.map((s) => s).join(', ')
                  : undefined
              }
            />

            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.type',
              })}
              value={item?.evidenceTypeName}
            />
            <ReadOnlyField
              label={formatMessage({ id: 'archivalUnit.form' })}
              value={formatMessage({
                id: `archivalUnit.entityCharacter.${item?.entityCharacter}`,
              })}
            />
          </Grid>
        </CollapseSection>
      </Paper>
      {/* POPIS */}
      <Paper elevation={0} className={classes.paper}>
        <CollapseSection
          title="Popis"
          id="description"
          overrideOpen={isExpanded('description')}
          registerKey={registerKey}
          onStateChange={handleStateChange}
        >
          <Grid container className={classes.root}>
            <ReadOnlyField label="Obsah (regest)" value={item?.summary} />
            <ReadOnlyField label="Datace" value={item?.timeRange} />
            <ReadOnlyField
              label="Formální název jednotky popisu"
              value={item?.formalTitle}
            />

            <ReadOnlyField
              label="Díl, část, pořadí vydání"
              value={item?.materialspec}
            />
            {/*CHYBA: */}
            {/*Autorské dílo (je formou odkazu do rejstříkových hesel): */}

            <ReadOnlyField
              label="Textový způsob zápisu datace"
              value={item?.fromTextual}
            />

            {/*CHYBA: */}
            {/*Jiné datace jednotky popisu než datace vzniku jednotky popisu (zobrazovat zkrácený název “Jiná datace”
: */}
          </Grid>
        </CollapseSection>
      </Paper>
      {/* Rozšířený popis */}
      <Paper elevation={0} className={classes.paper}>
        <CollapseSection
          title="Rozšířený popis"
          id="extended-description"
          overrideOpen={isExpanded('extended-description')}
          registerKey={registerKey}
          onStateChange={handleStateChange}
        >
          <Grid container className={classes.root}>
            {(item?.colophon?.originators ?? []).map((o) => (
              <RegisterField
                label="Původce"
                withoutBorder={true}
                value={o?.preferredName?.label}
                valueLink={o && handleOriginatorLink(o, true)}
              />
            ))}
            <ReadOnlyField
              label="Dějiny jednotky popisu"
              value={item?.custodHist}
            />
            <ReadOnlyField
              label="Způsob uspořádání jednotky popisu"
              value={item?.arrangement}
            />
            <ReadOnlyField
              label="Tematický popis jednotky popisu"
              value={item?.scopeContent}
            />
            <ReadOnlyField label="Přímý zdroj akvizice" value={item?.acqInfo} />
            <ReadOnlyField label="Pravidla zpracování" value={item?.rules} />
            <ReadOnlyField label="Budoucí přírůstky" value={item?.accruals} />
            <ReadOnlyField label="Fyzický stav" value={item?.physTech} />
            <ReadOnlyField
              label="Identifikace předlohy kopie"
              value={item?.originalsLoc}
            />
            <ReadOnlyField
              label="Způsob a forma dochování"
              value={item?.didPhysDesc}
            />
            <ReadOnlyField label="Měřítko" value={item?.scaleMaterialSpec} />
            <ReadOnlyField
              label="Orientace"
              value={item?.orientationMaterialSpec}
            />
            <ReadOnlyField label="Část" value={item?.volumeMaterialSpec} />
            {evidenceUnits?.map((eu, index) => (
              <Fragment key={index}>
                {Object.keys(eu)
                  .filter(
                    (k) =>
                      EvidenceUnitLabels[k as keyof EvidenceUnit]?.render ??
                      true
                  )
                  .map((k, kindex) => (
                    <ReadOnlyField
                      key={kindex}
                      label={
                        EvidenceUnitLabels?.[k as keyof EvidenceUnit]?.label ??
                        k
                      }
                      value={eu?.[k as keyof EvidenceUnit] ?? '??'}
                    />
                  ))}
              </Fragment>
            ))}
            {(item?.spaceOccupied ?? []).map((so, index) => (
              <ReadOnlyField
                key={`so-${index}-${so.unitKind}`}
                label={getSpaceOccupiedLabel(so)}
                value={so.quantity}
              />
            ))}
            <ReadOnlyField label="Kopie" value={item?.altFormAvail} />
            <ReadOnlyField
              label="Odkazy na příbuzné dokumenty"
              value={item?.relatedMaterial}
            />
            <ReadOnlyField
              label="Jazyk"
              value={item?.langMaterial?.join(', ')}
            />
            <ReadOnlyField
              label="Převažující jazyk"
              value={item?.majorityLangMaterial?.join(', ')}
            />
            <ReadOnlyField
              label="Podmínky přístupu"
              value={item?.accessRestrict}
            />
            <ReadOnlyField
              label="Edice a literatura"
              value={item?.bibliography}
            />
            <ReadOnlyField label="Veřejná poznámka" value={item?.note} />
          </Grid>
        </CollapseSection>
      </Paper>
      {(relations?.length ?? 0) > 0 && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            overrideOpen={isExpanded('register')}
            id="register"
            onStateChange={handleStateChange}
            registerKey={registerKey}
            title="Rejstříková hesla"
          >
            <Grid container className={classes.root}>
              {relations?.map((r) => (
                <RegisterField
                  key={r.id}
                  label={decideRelationLabel(r)}
                  value={constructOriginatorLabel(r.originator)}
                  withoutBorder={true}
                  valueLink={
                    r.originator && handleOriginatorLink(r.originator, true)
                  }
                />
              ))}
            </Grid>
          </CollapseSection>
        </Paper>
      )}
    </>
  );
};
